import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { PERMISSIONS } from '@ithelpdesk/shared';
import { useUnreadNotificationCount } from '../api/notifications';
import { useTicketCounts } from '../api/queries';
import { usePermission } from '../api/session';
import { useSystemHealth } from '../api/system-health';
import { useLocalStorageState } from '../ui/hooks';
import { AmbientFlow } from './AmbientFlow';
import { CommandPalette } from './CommandPalette';
import { SubmittedConfirmation } from './SubmittedConfirmation';
import { ErrorBoundary } from './ErrorBoundary';
import type { NavCounts } from './nav';
import { Sidebar } from './Sidebar';
import { useNavFlags } from './useNavFlags';
import { TopBar } from './TopBar';
import { installAudioUnlock } from '../features/live/notificationSound';
import { useClickRefresh } from '../features/live/useClickRefresh';
import styles from './AppShell.module.css';
import { brandName } from '../api/runtime-config';

/**
 * Route -> section label for `document.title` (the top bar itself shows no
 * page title — each screen owns its own h1 and the brand lives in the sidebar). Order
 * matters: `/tickets/new` must be matched before `/tickets/:id`.
 */
const PAGE_TITLES: Array<{ path: string; title: string }> = [
  { path: '/dashboard', title: 'Dashboard' },
  { path: '/tickets/new', title: 'New Ticket' },
  { path: '/tickets/:id', title: 'Ticket' },
  { path: '/tickets', title: 'All Tickets' },
  { path: '/my-tickets', title: 'My Tickets' },
  { path: '/unassigned', title: 'Unassigned' },
  { path: '/awaiting-confirmation', title: 'Awaiting Confirmation' },
  { path: '/resolved', title: 'Resolved' },
  { path: '/closed', title: 'Closed' },
  { path: '/notifications', title: 'Notifications' },
  { path: '/design-workflow/new', title: 'New Design Request' },
  { path: '/design-workflow/:id/edit', title: 'Edit Design Request' },
  { path: '/design-workflow/:id', title: 'Design Request' },
  { path: '/design-workflow', title: 'Design Workflow' },
  { path: '/admin/*', title: 'Admin' },
];

function usePageTitle(): string {
  const location = useLocation();
  const match = PAGE_TITLES.find((entry) => matchPath(entry.path, location.pathname));
  return match?.title ?? '';
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Nav badge counts from real, server-scoped data only. Badges count OPEN
 * work (labelled "open" via `NavItem.countLabel`), which is what the queue
 * needs attention for — the queue itself can list closed tickets too. `GET /tickets/counts`
 * already returns just what this actor may see; an END_USER's `open` is
 * their own open tickets. Closed shows `byStatus.CLOSED` from the same response.
 */
function useNavCounts(): NavCounts {
  const counts = useTicketCounts();
  const unread = useUnreadNotificationCount();
  const canReadTeam = usePermission(PERMISSIONS.TICKET_READ_TEAM);
  const canReadAny = usePermission(PERMISSIONS.TICKET_READ_ANY);
  const isStaff = canReadTeam || canReadAny;
  return useMemo(() => {
    const c = counts.data;
    const out: NavCounts = {};
    if (c) {
      if (isStaff) {
        out['/tickets'] = c.open;
        out['/my-tickets'] = c.assignedToMe;
        out['/unassigned'] = c.unassigned;
        out['/awaiting-confirmation'] = c.awaitingConfirmation;
        out['/resolved'] = c.resolved;
        // Real, server-scoped per-status count (same visibility as the rest).
        const closed = c.byStatus?.CLOSED;
        if (typeof closed === 'number') out['/closed'] = closed;
      } else {
        out['/my-tickets'] = c.open;
      }
    }
    const unreadCount = unread.data?.count;
    if (typeof unreadCount === 'number') out['/notifications'] = unreadCount;
    return out;
  }, [counts.data, unread.data, isStaff]);
}

/**
 * The authenticated layout: sidebar + top bar + routed content. Mounted once
 * by the router under `RequireAuth`; every screen renders through its
 * `<Outlet />`. Owns the global search shortcut (Ctrl/⌘+K, or "/" when not
 * typing in a field) — the only global shortcut in the app.
 */
export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const title = usePageTitle();
  const navCounts = useNavCounts();
  const navFlags = useNavFlags();
  const health = useSystemHealth();
  // Click anywhere (throttled) re-reads active server data; no page reload.
  useClickRefresh();
  // Per-viewer convenience only (try/catch-wrapped storage). Lives here, not in
  // the Sidebar, because the toggle is in the top bar.
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorageState('ithelpdesk.sidebar.collapsed', false);

  // The new-work chime may only start after a user gesture (autoplay policy).
  useEffect(() => {
    installAudioUnlock();
  }, []);

  useEffect(() => {
    document.title = title ? `${title} · ${brandName()}` : brandName();
  }, [title]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && k === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (k === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const closePalette = useCallback(() => setPaletteOpen(false), []);

  return (
    <div className={styles.shell}>
      {/* Decorative, theme-aware brand waves: ONE fixed layer behind the sidebar
          and the content so the ribbons run unbroken across both. Inert. */}
      <AmbientFlow />
      <a className={styles.skipLink} href="#main-content">
        Skip to main content
      </a>
      <Sidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        counts={navCounts}
        navFlags={navFlags}
        collapsed={sidebarCollapsed}
        health={health}
      />
      <div className={styles.main}>
        <TopBar
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          onMenuClick={() => setMobileNavOpen(true)}
          onSearchClick={() => setPaletteOpen(true)}
        />
        <main className={styles.content} id="main-content" tabIndex={-1}>
          {/* Keyed on the path so a crash on one screen doesn't keep the
              boundary tripped after navigating away from it. */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={closePalette} navFlags={navFlags} />
      <SubmittedConfirmation />
    </div>
  );
}
